import {
  Box,
  Container,
  Grid,
  LinearProgress,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import NavigationLock from "./NavigationLock";

import { useTranslation } from "common/locales";
import RelevantFilesList from "features/programmesActions/RelevantFilesList";
import {
  addFilledOut,
  fetchAllProgrammeActions,
  ProgrammeActions,
  selectProgrammeActionsById,
  selectSubmittedActionsEntriesByProgrammeId,
  useAppDispatch,
  useAppSelector,
} from "model";
import {
  setQuestionnaireFinished,
  setQuestionnaireStarted,
} from "model/questionnaireStartedSlice";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import styled, { css } from "styled-components";
import ActionList from "./ActionList";
import CenteredTextBox from "./CenteredTextBox";
import StepCard from "./FirstStepCard";
import ProgrammeBanner from "./ProgrammeBanner";

/**
 * Web-only component for displaying programme details.
 * This component is used only on desktop/web view.
 * Mobile continues to use ProgrammeDetailsDrawer in the drawer.
 */
export default function ProgrammeDetailsWeb() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { programmeId } = useParams<{ programmeId: string }>();
  const REFRESH_INTERVAL = 1 * 1000;
  const programmeActionsQuestionnaireFilledOutIds = useSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-return
    (state) => (state as any).programmeActionsQuestionnaireFilledOutIds
  );

  const [viewMore, setViewMore] = useState(false);
  const questionnaireStarted = useSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-return
    (state) => (state as any).questionnaireStarted
  );

  const { t } = useTranslation();
  const user = useAppSelector((state) => state.user.details);
  const history = useHistory();

  const dispatch = useAppDispatch();

  const programmeActions = useAppSelector((state) =>
    selectProgrammeActionsById(state, programmeId)
  );

  const actionPlanEntries = useAppSelector((state) =>
    selectSubmittedActionsEntriesByProgrammeId(state, programmeId)
  );

  let loading = useAppSelector(
    (state) => state.programmeActions.loading === "pending"
  );
  const actionsLoading = useAppSelector(
    (state) => state.submittedActions.loading === "pending"
  );

  loading = loading || actionsLoading;
  const isSubmitted = programmeActions?.submitted === true;

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleMessages = (content: any) => {
      if (!content) {
        return;
      }
      // Check if submission is completed.
      if (
        typeof content.data === "object" &&
        content.data.action === "submission-completed"
      ) {
        dispatch(addFilledOut({ filledOutProgrammeId: programmeId }));
        return;
      }
    };

    if (window.addEventListener) {
      window.addEventListener("message", handleMessages, true); // Capture
    }
    return () => {
      if (window.removeEventListener) {
        window.removeEventListener("message", handleMessages, true);
      }
    };
  }, [dispatch, programmeId]);

  useEffect(() => {
    const unsetQuestionnaireStarted = history.listen((e) => {
      if (e.pathname !== `/programme-details/${programmeId}`) {
        // Returning false allows navigation, returning a string shows a prompt
        dispatch(setQuestionnaireFinished(true));
      }
    });

    return function cleanup() {
      unsetQuestionnaireStarted();
    };
  }, [dispatch, history, programmeId]);

  useEffect(() => {
    const onBeforeUnload = (ev: Event) => {
      if (questionnaireStarted.questionnaireStarted) {
        ev.preventDefault();
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionnaireStarted.questionnaireStarted]);

  const filledOutButResultNotAvailableYet =
    programmeActionsQuestionnaireFilledOutIds.programmeActionsQuestionnaireFilledOutIds.includes(
      programmeId
    ) && !isSubmitted;

  useEffect(() => {
    if (filledOutButResultNotAvailableYet) {
      const reloadProgrammesAndActions = async () => {
        if (!loading) {
          await dispatch(fetchAllProgrammeActions());
        }
        return;
      };
      const refreshInterval = setInterval(
        reloadProgrammesAndActions,
        REFRESH_INTERVAL
      );
      // clear interval when there no fields with invalid data
      if (isSubmitted) {
        clearInterval(refreshInterval);
      }
      return () => clearInterval(refreshInterval);
    }
  }, [
    dispatch,
    filledOutButResultNotAvailableYet,
    loading,
    isSubmitted,
    REFRESH_INTERVAL,
    programmeActions,
  ]);

  if (loading || filledOutButResultNotAvailableYet) {
    return <LinearProgress />;
  }

  const maxHeight = viewMore ? "auto" : 200;

  const hasQuestionnaireUrl = !!programmeActions?.filloutId;

  // Display mode 1: Actions have been selected/submitted
  if (isSubmitted && actionPlanEntries.length > 0) {
    return (
      <Box sx={{ bgcolor: "white" }}>
        {ProgrammeDetailsWithBanner(
          programmeActions,
          viewMore,
          maxHeight,
          setViewMore,
          user?.name ?? "",
          programmeActions.welcomeText,
          t,
          false
        )}
        <Container
          maxWidth={false}
          disableGutters
          sx={{
            px: 3,
            pb: 3,
            pl: 5,
          }}
        >
          <Box
            sx={{
              bgcolor: "white",
              borderRadius: "8px",
              border: "1px solid #e4e4e7",
              boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.05)",
              p: 3,
            }}
          >
            <Typography
              variant="h5"
              component="h2"
              sx={{
                fontSize: "20px",
                fontWeight: 600,
                color: "#09090b",
                mb: 1.5,
              }}
            >
              Your action plan
            </Typography>
            <Grid container>
              <Grid
                item
                xs={12}
                sm={10}
                md={8}
                sx={{ display: "flex", justifyContent: "flex-start" }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: "#52525b",
                    fontSize: "14px",
                    mb: 3,
                  }}
                >
                  {t("action.plan.submitted.thank.you")}
                </Typography>
              </Grid>
            </Grid>

            <ActionList actionPlanEntries={actionPlanEntries} />
          </Box>
        </Container>
      </Box>
    );
  }

  // Display mode 1A: Actions have been selected/submitted but the result is not available yet because the processing ios not
  if (isSubmitted && actionPlanEntries.length === 0) {
    return (
      <Box sx={{ bgcolor: "white" }}>
        {ProgrammeDetailsWithBanner(
          programmeActions,
          viewMore,
          maxHeight,
          setViewMore,
          user?.name ?? "",
          t("action.plan.submitted.but.not.available.yet"),
          t,
          false
        )}
      </Box>
    );
  }

  // Display mode 2: Questionnaire is being filled out
  if (
    hasQuestionnaireUrl &&
    questionnaireStarted.questionnaireStarted &&
    programmeActions
  ) {
    return (
      <Box>
        <NavigationLock
          when={questionnaireStarted.questionnaireStarted}
        ></NavigationLock>

        {ProgrammeDetailsWithBanner(
          programmeActions,
          viewMore,
          maxHeight,
          setViewMore,
          user?.name ?? "",
          programmeActions.welcomeText,
          t
        )}

        <RelevantFilesList />
      </Box>
    );
  }

  // Display mode 3: Questionnaire not started yet
  if (
    hasQuestionnaireUrl &&
    !questionnaireStarted.questionnaireStarted &&
    programmeActions
  ) {
    return (
      <Box>
        {ProgrammeDetailsWithBanner(
          programmeActions,
          viewMore,
          maxHeight,
          setViewMore,
          user?.name ?? "",
          programmeActions.welcomeText,
          t
        )}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            px: 3,
          }}
        >
          <Box sx={{ maxWidth: "600px", width: "100%" }}>
            <StepCard
              stepNumber={1}
              stepTitle="Plan your actions"
              stepDescription="The first step is to plan actions that feel manageable and will make a real impact on your farm."
              buttonText="PLAN YOUR ACTIONS"
              daysLeft={122}
              status="In progress"
              onButtonClick={() => {
                dispatch(setQuestionnaireStarted(true));
              }}
            />
          </Box>
        </Box>
      </Box>
    );
  }

  // Fallback: Just show programme details
  if (programmeActions) {
    return (
      <Box>
        {ProgrammeDetailsWithBanner(
          programmeActions,
          viewMore,
          maxHeight,
          setViewMore,
          user?.name ?? "",
          programmeActions.welcomeText,
          t
        )}
      </Box>
    );
  }

  return <></>;
}

const MoreLessButton = styled("span")(
  ({ theme }) => css`
    color: var(--Dahlia-700, #2e7831);
    font-feature-settings: "liga" off, "clig" off;
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 16px;
  `
);

/**
 * Renders programme details with banner header (matching programmes list design)
 */
const ProgrammeDetailsWithBanner = (
  programmeActions: ProgrammeActions,
  viewMore: boolean,
  maxHeight: string | number,
  setViewMore: (arg: boolean) => void,
  userName: string,
  text: string,
  t: (a: string) => string,
  readMoreOrLessButton = true
) => {
  return (
    <Box sx={{ bgcolor: "white", p: 3 }}>
      {/* Banner Header */}
      <Box sx={{ mb: 3 }}>
        <ProgrammeBanner programme={programmeActions} borderRadius="8px" />
      </Box>

      {/* Programme Name */}
      <Grid
        container
        spacing={3}
        sx={{
          mb: 2,
          display: "flex",
          alignItems: "left",
          justifyContent: "flex-start",
          gap: 1,
        }}
      >
        <Grid item xs={12} sm={12} sx={{ textAlign: "left" }}>
          <Typography
            variant="h5"
            component="h2"
            sx={{
              fontSize: "20px",
              fontWeight: 600,
              color: "#09090b",
              mb: 1.5,
              paddingLeft: "18px",
            }}
          >
            {programmeActions.programmeName}
          </Typography>
        </Grid>

        {/* Text Content */}
        {text && (
          <Grid
            item
            xs={12}
            sm={10}
            md={8}
            sx={{ display: "flex", justifyContent: "flex-start" }}
          >
            <CenteredTextBox
              text={text.replace("FARMER_NAME", userName)}
              useMaxSpace={true}
              maxHeight="auto"
              lineHeight={20}
              variant="body2"
              style={{ color: "#666", margin: 0 }}
            />
          </Grid>
        )}
        {!text && (
          <Grid
            item
            xs={12}
            sm={10}
            md={8}
            sx={{ display: "flex", justifyContent: "flex-start" }}
          >
            <CenteredTextBox
              text={t("programme.in.developement")}
              useMaxSpace={true}
              maxHeight="auto"
              lineHeight={20}
              variant="body2"
              style={{ color: "#666", margin: 0 }}
            />
          </Grid>
        )}
      </Grid>
    </Box>
  );
};
